// src/pages/finance-portal/tax-nexus/TaxNexus.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    IconButton, Tooltip, Switch, FormControlLabel, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    APPROACHING: { bg: '#fef9c3', color: '#854d0e' },
    NOT_ESTABLISHED: { bg: '#f1f5f9', color: '#64748b' },
    EXPIRED: { bg: '#fecaca', color: '#991b1b' },
};

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC',
];

const STATE_NAMES = {
    AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
    CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
    IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
    ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
    MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
    NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
    OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
    TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
    WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'District of Columbia',
};

export default function TaxNexus() {
    const [nexus, setNexus] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Form Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({
        state_code: '', state_name: '',
        sales_threshold: '', transaction_threshold: '',
        physical_presence: false,
        registration_number: '',
    });
    const [saving, setSaving] = useState(false);

    // Delete Dialog
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Update Metrics
    const [updating, setUpdating] = useState(false);

    const fetchNexus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const res = await axiosInstance.get('/api/finance/tax/nexus', { params });
            if (res.data.success) {
                setNexus(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load nexus data');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage]);

    useEffect(() => { fetchNexus(); }, [fetchNexus]);

    const openForm = (item) => {
        if (item) {
            setEditData(item);
            setFormData({
                state_code: item.state_code || '',
                state_name: item.state_name || '',
                sales_threshold: item.sales_threshold ?? '',
                transaction_threshold: item.transaction_threshold ?? '',
                physical_presence: item.physical_presence || false,
                registration_number: item.registration_number || '',
            });
        } else {
            setEditData(null);
            setFormData({
                state_code: '', state_name: '',
                sales_threshold: '', transaction_threshold: '',
                physical_presence: false, registration_number: '',
            });
        }
        setFormOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                sales_threshold: formData.sales_threshold !== '' ? parseFloat(formData.sales_threshold) : null,
                transaction_threshold: formData.transaction_threshold !== '' ? parseInt(formData.transaction_threshold, 10) : null,
            };
            if (editData) {
                await axiosInstance.put(`/api/finance/tax/nexus/${editData.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/tax/nexus', payload);
            }
            setFormOpen(false);
            setSuccess(editData ? 'Nexus updated' : 'Nexus state added');
            fetchNexus();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save nexus');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/tax/nexus/${deleteId}`);
            setDeleteId(null);
            setSuccess('Nexus deleted');
            fetchNexus();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete nexus');
        } finally {
            setDeleting(false);
        }
    };

    const handleUpdateMetrics = async () => {
        setUpdating(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/tax/nexus/update-metrics');
            setSuccess('Nexus metrics updated');
            fetchNexus();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update metrics');
        } finally {
            setUpdating(false);
        }
    };

    const handleStateChange = (code) => {
        setFormData(prev => ({
            ...prev,
            state_code: code,
            state_name: STATE_NAMES[code] || '',
        }));
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PublicIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Nexus</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track sales tax nexus by state</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={updating ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <RefreshIcon />}
                            onClick={handleUpdateMetrics}
                            disabled={updating}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Update Metrics
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => openForm(null)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add State
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['State', 'Status', 'Physical Presence', 'Economic Nexus', 'Sales Threshold', 'Rolling 12M Sales', 'Threshold %', 'Registration #', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : nexus.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No nexus states configured</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : nexus.map(n => {
                                    const pct = n.threshold_percentage ?? 0;
                                    return (
                                        <TableRow key={n.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{n.state_name || n.state_code}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={getStatusLabel(n.status)} size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: (STATUS_COLORS[n.status] || STATUS_COLORS.NOT_ESTABLISHED).bg,
                                                        color: (STATUS_COLORS[n.status] || STATUS_COLORS.NOT_ESTABLISHED).color,
                                                    }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={n.physical_presence ? 'Yes' : 'No'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: n.physical_presence ? '#dcfce7' : '#f1f5f9', color: n.physical_presence ? '#166534' : '#64748b' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={n.economic_nexus ? 'Yes' : 'No'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: n.economic_nexus ? '#dbeafe' : '#f1f5f9', color: n.economic_nexus ? '#1e40af' : '#64748b' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{n.sales_threshold != null ? formatCurrency(n.sales_threshold) : '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{n.rolling_12m_sales != null ? formatCurrency(n.rolling_12m_sales) : '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(pct, 100)}
                                                        sx={{
                                                            flex: 1, height: 8, borderRadius: 4,
                                                            bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                borderRadius: 4,
                                                                bgcolor: pct >= 100 ? '#ef4444' : pct >= 75 ? '#f59e0b' : ACCENT,
                                                            },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: '#475569', minWidth: 40 }}>
                                                        {pct.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{n.registration_number || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => openForm(n)}>
                                                            <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => setDeleteId(n.id)}>
                                                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{editData ? 'Edit Nexus State' : 'Add Nexus State'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="State" required
                                value={formData.state_code}
                                onChange={e => handleStateChange(e.target.value)}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.map(s => <MenuItem key={s} value={s} sx={{ fontFamily }}>{s} - {STATE_NAMES[s]}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="State Name"
                                value={formData.state_name}
                                onChange={e => setFormData(prev => ({ ...prev, state_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Sales Threshold" type="number"
                                value={formData.sales_threshold}
                                onChange={e => setFormData(prev => ({ ...prev, sales_threshold: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Transaction Threshold" type="number"
                                value={formData.transaction_threshold}
                                onChange={e => setFormData(prev => ({ ...prev, transaction_threshold: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.physical_presence}
                                        onChange={e => setFormData(prev => ({ ...prev, physical_presence: e.target.checked }))}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontWeight: 600 }}>Physical Presence</Typography>}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Registration #"
                                value={formData.registration_number}
                                onChange={e => setFormData(prev => ({ ...prev, registration_number: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving || !formData.state_code}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editData ? 'Update' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Nexus State?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteId(null)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
